import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'moment-duration-format';
import 'scrollingelement';
import 'transitionEnd';
import {loadableReady} from '@loadable/component';
import isNil from 'lodash/isNil';
import React from 'react';
import {hydrate} from 'react-dom';
import {Provider} from 'react-redux';
import {Router} from 'react-router-dom';
import APIUtils from '@spothero/utils/api';
import createStore, {getHistory} from 'store/store';
import SegmentUtils from 'utils/segment';
import startup from './startup';
import App from './App';
import {HelmetProvider} from 'react-helmet-async';

// these two imports here are for HMR to work locally
// root cause: we do not import scss at jsx level and HMR
// has no association with these files being in the browser
// due to how renderer.js includes the <link
// this is a creative workaround (hack)
// a change will occur, and all HMR updates will be pushed
// based on these two files, yes it's excessive
// TODO: setup CW styles to support scss imports at a JSX file level
// which includes removing the renderer.js <link and using extractor.getStyleTags()
if (process.env.NODE_ENV === 'development') {
    require('sass/desktop.scss');
    require('sass/mobile.scss');
}

const appData = window.__INITIAL_DATA__; // eslint-disable-line no-underscore-dangle
const {
    configData,
    initialState,
    destination,
    pageType,
    optimizelyDatafile,
    optimizelyCookies,
    ttfrTimer,
    helmetContext,
} = appData;
const {
    searchRequest: {isSearchPage, starts},
    user: {data: user},
} = initialState;

// Fix for retries resulting from a relative base path.
// This axios PR contains a likely fix and was merged in Sep 2019: https://github.com/axios/axios/pull/2391
// We can remove this line after a new version of axios is published to npm
configData.spotHeroAPIBasePath = `${configData.siteUrl}${configData.spotHeroAPIBasePath}`;

APIUtils.init({
    baseUrl: configData.spotHeroAPIBasePath,
    additionalHeaders: {
        Authorization: null,
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-spothero-spa': 'consumer-web', // Used by DevTools to track web application traffic
        'SpotHero-Version': '2019-08-13',
    },
});

if (isSearchPage && !isNil(starts)) {
    // every search page that has a map would also have a starts date set,
    // whereas pages like airport search that is a landing page wouldn't have starts set
    SegmentUtils.startTimeToFirstResultTimer({
        isInitialLoad: true,
        timerAddition: ttfrTimer,
    });
}

const render = async () => {
    await startup({
        configData,
        isBrowser: true,
        optimizelyData: optimizelyDatafile,
        serverCookies: optimizelyCookies,
        pageType,
        destination,
        user,
    });

    await loadableReady(() => {
        hydrate(
            <HelmetProvider context={helmetContext}>
                <Provider store={createStore(initialState)}>
                    <Router history={getHistory()}>
                        <App {...appData} />
                    </Router>
                </Provider>
            </HelmetProvider>,
            document.getElementById('root')
        );
    });

    delete window.__INITIAL_DATA__; // eslint-disable-line no-underscore-dangle
};

render();
